/**
 * InsightsSourceCard Component
 * Collapsible card displaying a source category and its items
 *
 * Pattern: Adapted from DataSourceCard.tsx for client insights
 * Features:
 * - Collapsible with smooth animation
 * - Status indicators (pending, loading, complete, error)
 * - Item count display
 * - Loading progress bar
 * - Icon + title header
 */

import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { InsightsSource, InsightsSourceType } from '../../../types/insights';
import InsightItemCard from './InsightItemCard';

interface InsightsSourceCardProps {
  title: string;
  icon: string; // Emoji icon (e.g., "📧")
  source: InsightsSource;
  sourceType: InsightsSourceType;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const InsightsSourceCard: React.FC<InsightsSourceCardProps> = ({
  title,
  icon,
  source,
  sourceType: _sourceType,
  isExpanded,
  onToggleExpand,
}) => {
  // Determine if card can be expanded
  const canExpand = source.status === 'complete' && source.count > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 transition-all shadow-sm">
      {/* Card Header - Always Visible */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Left: Icon + Info */}
          <div className="flex items-center gap-3">
            <span className="text-2xl" aria-label={title}>
              {icon}
            </span>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>

              {/* Status Text */}
              {source.status === 'complete' && (
                <p className="text-xs text-gray-600">
                  {source.count} {source.count === 1 ? 'item' : 'items'}
                </p>
              )}
              {source.status === 'loading' && (
                <p className="text-xs text-blue-600">Loading...</p>
              )}
              {source.status === 'error' && (
                <p className="text-xs text-red-600">Failed to load</p>
              )}
              {source.status === 'pending' && (
                <p className="text-xs text-gray-500">Pending...</p>
              )}
            </div>
          </div>

          {/* Right: Expand/Collapse Button */}
          {canExpand && (
            <button
              onClick={onToggleExpand}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md flex items-center gap-1 transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <>
                  Collapse <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Expand <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Progress Bar (only during loading) */}
        {source.status === 'loading' && (
          <div className="mt-3 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full animate-pulse"
              style={{ width: '60%' }}
            />
          </div>
        )}
      </div>

      {/* Expanded Content - Item List */}
      {isExpanded && source.status === 'complete' && (
        <div className="border-t border-gray-200 p-4 pt-3 space-y-2 bg-gray-50">
          {source.items.length > 0 ? (
            source.items.map((item) => (
              <InsightItemCard key={item.id} item={item} />
            ))
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">No items found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InsightsSourceCard;
